import type {
  BookingFlag,
  Deposit,
  InvoiceStatus,
  RefsLocation,
  RescheduleReason,
} from '@/domain/types';

/**
 * Display labels for reschedule reasons. Shared by the booking-detail history
 * list and the calendar reschedule-reason picker so both read identically.
 */
export const RESCHEDULE_REASON_LABEL: Record<RescheduleReason, string> = {
  'gap-fill': 'Gap fill',
  'artist-move': 'Artist move',
  'client-move': 'Client move',
  'cancellation-fill': 'Cancellation fill',
  other: 'Other',
};

/**
 * Deposit paid, derived from the ledger per the studio's real accounting:
 * paid + topup + rollover-in − rollover-out − forfeit. Falls back to the flat
 * `amountPaid` when no ledger is present so older seeds still show a figure.
 */
export function depositPaidAud(deposit: Deposit | undefined): number {
  if (!deposit) return 0;
  if (deposit.ledger && deposit.ledger.length > 0) {
    return deposit.ledger.reduce((sum, e) => {
      switch (e.kind) {
        case 'paid':
        case 'topup':
        case 'rollover-in':
        case 'reinstate': // management reversal of a forfeit — money is back
          return sum + e.amountAud;
        case 'rollover-out':
        case 'forfeit':
          return sum - e.amountAud;
        default:
          return sum; // refunded / transfer-in / transfer-out excluded from "paid"
      }
    }, 0);
  }
  return deposit.amountPaid ?? (deposit.status === 'paid' ? deposit.amountAud : 0);
}

// Flag → label + tone. Colours follow the Vault lead-colour system mapped onto
// muted variants that read on the dark ink canvas. Full literal classes so
// Tailwind's JIT keeps them.
export const FLAG_STYLE: Record<BookingFlag, { label: string; cls: string }> = {
  'medical-note': { label: 'Medical note', cls: 'border-blood/50 bg-blood/15 text-blood' },
  'deposit-topup-pending': {
    label: 'Top-up pending',
    cls: 'border-gold-bright/50 bg-gold/15 text-gold-bright',
  },
  'late-fill': { label: 'Late fill', cls: 'border-azure/40 bg-azure/15 text-azure' },
};

export const REFS_LOCATION_LABEL: Record<RefsLocation, string> = {
  uploaded: 'Uploaded',
  'on-client-card': 'On client card',
  'with-artist': 'With artist',
  'ig-dms': 'IG DMs',
  'physical-folder': 'Physical folder',
};

export const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  'not-issued': 'Not invoiced',
  draft: 'Invoice draft',
  issued: 'Invoice issued',
  paid: 'Invoice paid',
  'uninvoiced-writeoff': 'Write-off',
};
